import React from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { phoneMediaQuery, rescheduleTheme } from '../rescheduleTheme';
import { RescheduleTypography } from './RescheduleTypography';

type StepperProps = {
  steps: { key: string; label: string }[];
  currentIndex: number;
};

export const Stepper = ({ steps, currentIndex }: StepperProps) => {
  const { t } = useTranslation();
  const isPhone = useMediaQuery(phoneMediaQuery);
  const ariaLabel = t('manage_booking.v2.progress');

  if (isPhone) {
    return (
      <Grid container role="group" aria-label={ariaLabel} flexDirection="column" flexWrap="nowrap" gap="8px">
        <Grid container aria-current="step" justifyContent="space-between" flexWrap="nowrap">
          <RescheduleTypography style={{ fontSize: 13, color: rescheduleTheme.textMuted }}>
            {t('manage_booking.v2.stepOf', { n: currentIndex + 1, m: steps.length })}
          </RescheduleTypography>
          <RescheduleTypography style={{ fontSize: 13, color: rescheduleTheme.text, fontWeight: 600 }}>
            {steps[currentIndex]?.label}
          </RescheduleTypography>
        </Grid>
        <Grid
          container
          aria-hidden="true"
          sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, gap: '4px' }}>
          {steps.map((step, index) => (
            <Grid
              item
              key={step.key}
              sx={{
                height: '4px',
                borderRadius: '2px',
                background: index <= currentIndex ? rescheduleTheme.brand : rescheduleTheme.brandTint,
              }}
            />
          ))}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      component="ol"
      container
      aria-label={ariaLabel}
      flexWrap="nowrap"
      alignItems="center"
      justifyContent="center"
      sx={{ listStyle: 'none', margin: 0, padding: 0, gap: '14px' }}>
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isDone = index < currentIndex;
        return (
          <Grid
            component="li"
            container
            key={step.key}
            aria-current={isCurrent ? 'step' : undefined}
            flexWrap="nowrap"
            alignItems="center"
            width="auto"
            gap="10px">
            <Grid
              container
              flexWrap="nowrap"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: isCurrent ? rescheduleTheme.brand : isDone ? rescheduleTheme.brandTint : '#FFFFFF',
                border: `1.5px solid ${isCurrent || isDone ? rescheduleTheme.brand : rescheduleTheme.border}`,
                color: isCurrent ? '#FFFFFF' : isDone ? rescheduleTheme.brand : rescheduleTheme.textMuted,
              }}>
              <RescheduleTypography component="span" style={{ fontSize: 14, fontWeight: 600 }}>
                {index + 1}
              </RescheduleTypography>
            </Grid>
            <RescheduleTypography
              component="span"
              style={{
                fontSize: 14,
                color: isCurrent ? rescheduleTheme.text : rescheduleTheme.textMuted,
                fontWeight: isCurrent ? 600 : 400,
              }}>
              {step.label}
            </RescheduleTypography>
            {index < steps.length - 1 && (
              <Grid
                item
                aria-hidden="true"
                sx={{ width: '40px', height: '1.5px', background: rescheduleTheme.border }}
              />
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};
